import type { JSX } from 'solid-js';
import {
  For,
  createEffect,
  createMemo,
  createSignal,
  mergeProps,
  on,
  onCleanup,
} from 'solid-js';
import type { DynamicProps } from 'solid-js/web';
import { Dynamic } from 'solid-js/web';
import { omitProps } from 'solid-use/props';

type OmitAndMerge<T, U> = T & Omit<U, keyof T>;

// Helper functions for column calculations
function getShortestColumn(columns: number[]): number {
  let min = 0;
  let record = Number.MAX_SAFE_INTEGER;

  for (let i = 0, len = columns.length; i < len; i += 1) {
    if (columns[i] < record) {
      record = columns[i];
      min = i;
    }
  }

  return min;
}

function getLongestColumn(columns: number[]): number {
  let min = 0;
  let record = 0;

  for (let i = 0, len = columns.length; i < len; i += 1) {
    if (columns[i] > record) {
      record = columns[i];
      min = i;
    }
  }

  return min;
}

// Content width calculation
function getContentWidth(el: HTMLElement | SVGAElement): number {
  const styles = getComputedStyle(el);

  return (
    el.clientWidth -
    Number.parseFloat(styles.paddingLeft) -
    Number.parseFloat(styles.paddingRight)
  );
}

// Mason component styling constants
const MASON_STYLE: JSX.CSSProperties = {
  position: 'relative',
  width: '100%',
  'max-width': '100%',
};

const MASON_STYLE_STRING = ';position:relative;width:100%;max-width:100%;';

const MASON_KEY = 'data-solid-mason';

// Mason state interface
interface MasonState {
  width: number;
  columns: number[];
  elements: HTMLElement[];
  heights: number[];
}

// RAF Debounce utility
function createRAFDebounce(callback: () => void): () => void {
  let timeout: number;

  onCleanup(() => {
    cancelAnimationFrame(timeout);
  });

  return () => {
    if (timeout) {
      cancelAnimationFrame(timeout);
    }

    timeout = requestAnimationFrame(() => {
      callback();
    });
  };
}

// Mason component implementation
function createMason(el: HTMLElement, state: MasonState): void {
  // Get computed style
  const columnCount = state.columns.length;
  const containerWidth = getContentWidth(el);
  let isAllDirty = containerWidth !== state.width;
  const widthPerColumn = containerWidth / columnCount;

  const newColumns: number[] = new Array<number>(state.columns.length).fill(0);
  if (isAllDirty) {
    state.columns = [...newColumns];
  }

  let node = el.firstElementChild;
  let nodeIndex = 0;

  while (node) {
    if (node instanceof HTMLElement) {
      const targetColumn = getShortestColumn(newColumns);
      if (isAllDirty || state.elements[nodeIndex] !== node) {
        // Set the width of the node
        node.style.width = `${widthPerColumn}px`;
        // Set the position
        node.style.position = 'absolute';
        // Set the top/left
        const currentColumnHeight = newColumns[targetColumn];
        node.style.top = `${currentColumnHeight}px`;
        node.style.left = `${targetColumn * widthPerColumn}px`;
        // Increase column height
        node.getBoundingClientRect();
        const nodeHeight = node.offsetHeight;
        state.elements[nodeIndex] = node;
        state.heights[nodeIndex] = nodeHeight;
        isAllDirty = true;
        newColumns[targetColumn] = currentColumnHeight + nodeHeight;
      } else {
        newColumns[targetColumn] += state.heights[nodeIndex];
      }
      nodeIndex += 1;
    }
    node = node.nextElementSibling;
  }
  const targetColumn = getLongestColumn(newColumns);
  const currentColumnHeight = newColumns[targetColumn];
  el.style.height = `${currentColumnHeight}px`;
  state.width = containerWidth;
  state.columns = newColumns;
}

// Mason props type
type MasonProps<
  Data,
  T extends keyof JSX.HTMLElementTags = 'div',
> = OmitAndMerge<
  {
    as?: T;
    columns: number;
    items?: Data[] | null | undefined;
    children: (item: Data, index: () => number, columnWidth: number) => JSX.Element;
    style?: JSX.CSSProperties | string;
  },
  JSX.HTMLElementTags[T]
>;

// Main Mason component
export function Mason<Data, T extends keyof JSX.HTMLElementTags = 'div'>(
  props: MasonProps<Data, T>,
): JSX.Element {
  const [ref, setRef] = createSignal<HTMLElement>();
  const [columnWidth, setColumnWidth] = createSignal(0);

  createEffect(() => {
    const el = ref();
    if (el) {
      // Set style
      el.style.position = 'relative';
      el.style.width = '100%';
      el.style.maxWidth = '100%';

      const state: MasonState = {
        width: 0,
        columns: new Array<number>(props.columns).fill(0),
        elements: [],
        heights: [],
      };

      const recalculate = createRAFDebounce(() => {
        const containerWidth = getContentWidth(el);
        const calculatedColumnWidth = containerWidth / props.columns;
        setColumnWidth(calculatedColumnWidth);
        createMason(el, state);
      });

      createMemo(
        on(
          () => props.items,
          () => {
            recalculate();
          },
        ),
      );

      // Track window resize
      window.addEventListener('resize', recalculate, { passive: true });
      onCleanup(() => {
        window.removeEventListener('resize', recalculate);
      });

      // Track child mutations
      const observer = new MutationObserver(mutations => {
        if (mutations.length) {
          recalculate();
        }
      });

      observer.observe(el, {
        childList: true,
      });

      onCleanup(() => {
        observer.disconnect();
      });
    }
  });

  return Dynamic<T>(
    mergeProps(
      {
        get component() {
          return props.as ?? 'div';
        },
        ref: setRef,
        get children() {
          return For({
            get each() {
              return props.items;
            },
            children(item, index) {
              return Dynamic({
                component: 'div',
                get children() {
                  return props.children(item, index, columnWidth());
                },
                style: {
                  position: 'absolute',
                },
              });
            },
          });
        },
        get [MASON_KEY]() {
          return props.columns;
        },
        get style() {
          const current = props.style;
          if (typeof current === 'string') {
            return `${current}${MASON_STYLE_STRING}`;
          }
          if (current) {
            return { ...current, ...MASON_STYLE };
          }
          return MASON_STYLE_STRING;
        },
      },
      omitProps(props, ['as', 'children', 'columns', 'style']),
    ) as unknown as DynamicProps<T>,
  );
}